import React, {useContext, useState} from 'react';
import {useMutation, useQuery} from "@tanstack/react-query";
import {changePassword, userInfo} from "../apis/userApi";
import {LanguageContext} from "../components/LanguageContext";
import Grid from "@mui/material/Grid2";
import {
    Alert, Box,
    Button, Dialog, DialogActions,
    DialogContent,
    DialogTitle,
    LinearProgress,
    Paper,
    Stack, TextField,
    Typography
} from "@mui/material";
import AccountCircleTwoToneIcon from '@mui/icons-material/AccountCircleTwoTone';
import {Item} from "../components/Item";
import Avatar from "@mui/material/Avatar";
import LockOpenTwoToneIcon from '@mui/icons-material/LockOpenTwoTone';
import Notification from "../components/Notification";

const ProfilePage = () => {
    const {translations} = useContext(LanguageContext);
    const id = localStorage.getItem('user_id');

    const [oldPasswordError, setOldPasswordError] = useState(false);
    const [oldPasswordErrorMessage, setOldPasswordErrorMessage] = useState('');

    const [newPasswordError, setNewPasswordError] = useState(false);
    const [newPasswordErrorMessage, setNewPasswordErrorMessage] = useState('');

    const [confirmPasswordError, setConfirmPasswordError] = useState(false);
    const [confirmPasswordErrorMessage, setConfirmPasswordErrorMessage] = useState('');

    const [type, setType] = useState(null);
    const [openN, setOpenN] = useState(false);
    const [message, setMessage] = useState("");

    const {data, isLoading, isError} = useQuery({
        queryKey: ['userInfo', id],
        queryFn: () => userInfo(id)
    });

    const changePasswordMutation = useMutation({
        mutationFn: (formData) => changePassword(formData),
        onSuccess: (response) => {
            setType(1);
            setMessage(response.data.message);
            setOpenN(true);

            handleClose();
        },
        onError: (error) => {
            setType(2);
            setMessage(error.response.data.message);
            setOpenN(true);
        }
    })

    const [open, setOpen] = useState(false);

    const handleClickOpen = () => {
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
    };

    const handleChangePassword = (event) => {
        event.preventDefault();

        const data = new FormData(event.target);

        if (!data.get('old_password') || data.get('old_password').length < 6) {
            setOldPasswordError(true);
            setOldPasswordErrorMessage(translations.profileOldPasswordShort);
            return;
        } else {
            setOldPasswordError(false);
            setOldPasswordErrorMessage('');
        }

        if (!data.get('new_password') || data.get('new_password').length < 6) {
            setNewPasswordError(true);
            setNewPasswordErrorMessage(translations.profileNewPasswordShort);
            return;
        } else {
            setNewPasswordError(false);
            setNewPasswordErrorMessage('');
        }

        if (!data.get('confirm_password') || data.get('confirm_password').length < 6) {
            setConfirmPasswordError(true);
            setConfirmPasswordErrorMessage(translations.profileConfirmPasswordShort);
            return;
        } else {
            setConfirmPasswordError(false);
            setConfirmPasswordErrorMessage('');
        }

        if (data.get('new_password') !== data.get('confirm_password')) {
            setConfirmPasswordError(true);
            setConfirmPasswordErrorMessage(translations.profileConfirmMismatch);
            return;
        } else {
            setConfirmPasswordError(false);
            setConfirmPasswordErrorMessage('');
        }

        if (data.get('new_password') === data.get('old_password')) {
            setNewPasswordError(true);
            setNewPasswordErrorMessage(translations.profileOldNewSame);
            return;
        } else {
            setNewPasswordError(false);
            setNewPasswordErrorMessage('');
        }

        data.set("id", id);

        changePasswordMutation.mutateAsync(data).catch((err) => {});
    }

    return (
        <Grid container spacing={2}>
            <Notification
                type={type}
                message={message}
                open={openN}
                setOpen={setOpenN}
            />
            <Grid size={{xs: 12}}>
                <Paper sx={{p: 2, justifyContent: "center", alignItems: "center"}}>
                    <Typography sx={{fontSize: 22, textAlign: "center"}}>
                        <AccountCircleTwoToneIcon size={22} sx={{mr: 2}}/>
                        {translations.profileTitle}
                    </Typography>
                </Paper>
            </Grid>
            <Grid size={{xs: 12}}>
                <Paper sx={{p: 2, justifyContent: "center", alignItems: "center"}}>
                    {isError ? (
                        <Alert severity="error">{translations.errorLoading}</Alert>
                    ) : isLoading ? (
                        <LinearProgress size={24} color="inherit"/>
                    ) : (
                        <Stack spacing={2}>
                            <Avatar sx={{width: 80, height: 80, alignSelf: "center"}}>
                                <AccountCircleTwoToneIcon />
                            </Avatar>
                            <Item>{translations.profileName}: {data?.data.ime}</Item>
                            <Item>{translations.profileEmail}: {data?.data.email}</Item>
                            <Item>{translations.profileStatus}: {data?.data.aktivnost ? <b style={{color: 'green'}}>{translations.profileActive}</b> : <b style={{color: 'red'}}>{translations.profileDeactivated}</b>}</Item>
                            <Item>{translations.profileCreatedAt}: {data?.data.datum_kreiranja}</Item>
                        </Stack>
                    )}
                </Paper>
            </Grid>
            <Grid size={{xs: 12}}>
                <Paper sx={{p: 2, justifyContent: "center", alignItems: "center"}}>
                    {isError ? (
                        <Alert severity="error">{translations.errorLoading}</Alert>
                    ) : isLoading ? (
                        <LinearProgress size={24} color="inherit"/>
                    ) : (
                        <Button
                            onClick={handleClickOpen}
                            endIcon={<LockOpenTwoToneIcon/>}
                            fullWidth
                            variant="contained"
                        >
                            {translations.profileChangePassword}
                        </Button>
                    )}
                </Paper>
            </Grid>
            <Dialog
                open={open}
                onClose={handleClose}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
            >
                <DialogTitle id="alert-dialog-title">
                    {translations.profileChangePasswordTitle}
                </DialogTitle>
                <DialogContent>
                    <Box component="form" noValidate onSubmit={handleChangePassword}>
                        <TextField
                            error={oldPasswordError}
                            helperText={oldPasswordErrorMessage}
                            name="old_password"
                            placeholder="••••••"
                            label={translations.profileOldPassword}
                            type="password"
                            id="old_password"
                            required
                            fullWidth
                            variant="outlined"
                            margin="normal"
                            color={oldPasswordError ? 'error' : 'primary'}
                        />
                        <TextField
                            error={newPasswordError}
                            helperText={newPasswordErrorMessage}
                            name="new_password"
                            placeholder="••••••"
                            label={translations.profileNewPassword}
                            type="password"
                            id="new_password"
                            required
                            fullWidth
                            variant="outlined"
                            margin="normal"
                            color={newPasswordError ? 'error' : 'primary'}
                        />
                        <TextField
                            error={confirmPasswordError}
                            helperText={confirmPasswordErrorMessage}
                            name="confirm_password"
                            placeholder="••••••"
                            label={translations.profileConfirmPassword}
                            type="password"
                            id="confirm_password"
                            required
                            fullWidth
                            variant="outlined"
                            margin="normal"
                            color={confirmPasswordError ? 'error' : 'primary'}
                        />
                        <DialogActions>
                            <Button onClick={handleClose} color="error">{translations.cancel}</Button>
                            <Button type="submit" variant="contained" color="success" onClick={handleClose} autoFocus>
                                {translations.confirm}
                            </Button>
                        </DialogActions>
                    </Box>
                </DialogContent>
            </Dialog>
        </Grid>
    );
};

export default ProfilePage;